import type React from 'react';
import { HistoryDrawerView } from './HistoryDrawerView';
import { useHistoryDrawer } from './useHistoryDrawer';

type HistoryDrawerProps = {
    /** The template whose history to show; `null` keeps the drawer closed. */
    operationKey: string | null;
    onClose: () => void;
    /**
     * Open the template in the editor — the footer's way through to an open
     * draft. `null` from a host that *is* the editor, which has nowhere to go.
     */
    onOpenEditor?: ((key: string) => void) | null;
};

/**
 * A template's whole configuration history, over whatever screen asked for it.
 *
 * Every version it has ever had, with the window it applied over, who published
 * it, how many entries posted under it, and a line-level diff against its
 * predecessor — then the log of who changed what and when. It is the answer to
 * the question the whole module is built around: *why did this entry post to
 * those accounts?*
 *
 * **Self-served, so both hosts can mount their own.** The catalog row and the
 * editor header each render one; neither owns the other's copy, because the
 * drawer owns its read. That is what makes a drawer possible here at all — while
 * the data belonged to a host, this had to be a sub-page.
 *
 * No `ErrorBoundary`: a failed read is reported inside the body. Throwing from a
 * drawer would tear down the working screen behind it over one template that
 * would not open.
 */
export const HistoryDrawer: React.FC<HistoryDrawerProps> = ({
    operationKey,
    onClose,
    onOpenEditor = null,
}) => {
    const drawer = useHistoryDrawer({ operationKey });

    return (
        <HistoryDrawerView
            accounts={drawer.accounts}
            canEdit={drawer.canEdit}
            draftVersion={drawer.draftVersion}
            expanded={drawer.expanded}
            hasError={drawer.hasError}
            history={drawer.history}
            isLoading={drawer.isLoading}
            locale={drawer.locale}
            onClose={onClose}
            onOpenActivity={drawer.onOpenActivity}
            onOpenEditor={onOpenEditor}
            onToggle={drawer.onToggle}
            operationKey={operationKey}
            timeline={drawer.timeline}
        />
    );
};
